// =============================================================================
// src/views/workspace/workspace-filter.ts — Filtro (nav/tag/busca) + ordenação.
//
// Funções PURAS sobre a lista de análises. O filtro é o que a nav lateral define
//   (categoria + tag + busca); a ordenação é ortogonal (chips do topo). "Recentes"
//   é um top-N por createdAtMs calculado sobre a lista inteira — por isso precisa
//   do conjunto recente antes de filtrar item a item.
//
// SRP: só lógica de seleção/ordenação. Sem DOM, sem estado, sem store.
// =============================================================================

import type {
  WorkspaceAnalysis,
  WorkspaceCategory,
  WorkspaceFilterState,
  WorkspaceSort,
  WorkspaceTag,
} from './workspace-types';
import { WORKSPACE_RECENT_COUNT } from './workspace-seed';

/** Aplica categoria + tag + busca (todos devem passar). */
export function filterAnalyses(
  all: readonly WorkspaceAnalysis[],
  state: WorkspaceFilterState,
): readonly WorkspaceAnalysis[] {
  const recentIds = recentIdSet(all);
  return all.filter(
    (analysis) =>
      matchesCategory(analysis, state.category, recentIds) &&
      matchesTag(analysis, state.tag) &&
      matchesSearch(analysis, state.search),
  );
}

/** Reordena (cópia) por critério dos chips: recência, alfabética ou mais vistas. */
export function sortAnalyses(
  list: readonly WorkspaceAnalysis[],
  sort: WorkspaceSort,
): readonly WorkspaceAnalysis[] {
  const copy = [...list];
  if (sort === 'alpha') return copy.sort((a, b) => a.title.localeCompare(b.title, 'pt-BR'));
  if (sort === 'views') return copy.sort((a, b) => b.viewsCount - a.viewsCount);
  return copy.sort((a, b) => b.createdAtMs - a.createdAtMs);
}

/** Contagem por categoria da nav (para os badges numéricos da sidebar). */
export function countByCategory(
  all: readonly WorkspaceAnalysis[],
): Record<WorkspaceCategory, number> {
  return {
    mine: all.filter((analysis) => analysis.category === 'mine').length,
    shared: all.filter((analysis) => analysis.category === 'shared').length,
    team: all.filter((analysis) => analysis.category === 'team').length,
    recent: recentIdSet(all).size,
    favorites: all.filter((analysis) => analysis.favorite).length,
  };
}

/** Ids das WORKSPACE_RECENT_COUNT análises mais recentes por createdAtMs. */
function recentIdSet(all: readonly WorkspaceAnalysis[]): Set<string> {
  const recent = [...all]
    .sort((a, b) => b.createdAtMs - a.createdAtMs)
    .slice(0, WORKSPACE_RECENT_COUNT)
    .map((analysis) => analysis.id);
  return new Set(recent);
}

/** Categoria: 'recent'/'favorites' são vistas calculadas; demais comparam o bucket. */
function matchesCategory(
  analysis: WorkspaceAnalysis,
  category: WorkspaceCategory,
  recentIds: Set<string>,
): boolean {
  if (category === 'recent') return recentIds.has(analysis.id);
  if (category === 'favorites') return analysis.favorite;
  return analysis.category === category;
}

/** Tag única (toggle): sem tag passa tudo; com tag exige tê-la. */
function matchesTag(analysis: WorkspaceAnalysis, tag: WorkspaceTag | null): boolean {
  if (tag === null) return true;
  return analysis.tags.includes(tag);
}

/** Busca por substring (minúsculo) em título OU tags; query vazia passa tudo. */
function matchesSearch(analysis: WorkspaceAnalysis, search: string): boolean {
  const query = search.trim().toLowerCase();
  if (query === '') return true;
  if (analysis.title.toLowerCase().includes(query)) return true;
  return analysis.tags.some((tag) => tag.toLowerCase().includes(query));
}
